import { Immutable } from "immer";
import React from "react";

import type { ToppersImageLinkBlock as Value } from "@reactivated";

// NOTE: faithful port of the legacy `streamfield_image_link.html`:
//   <a href="{{ self.page.url }}">{% image self.image width-400 %}</a>
// The anchor always renders (href collapses to "" when the page chooser is
// unset, matching the legacy `{{ self.page.url }}` interpolation). The
// `{% image ... width-400 %}` tag renders *nothing* when the image is null, so
// the <img> is gated on `v.image` (mirroring ComparisonCarouselBlock's
// renderProductImage → null). The `{% image %}` tag's default alt is the image's
// `default_alt_text`.
//
// WIDTH/HEIGHT (load-bearing): Wagtail's `{% image %}` tag emits explicit
// `width`/`height` attributes (the rendition's pixel dimensions). The toppers
// `_legal-page.scss` constrains only `.block-image_link img { max-width: 100% }`
// -- there is NO `height: auto`. So when the 50%-width image_link column is
// narrower than the rendition (i.e. at Mobile/Print widths, where 50% of the
// content box < 400px), the browser clamps the rendered WIDTH to the column but
// honors the literal `height` attribute, producing the legacy distorted
// (taller-than-wide) image. Dropping the attributes let the browser preserve the
// natural aspect instead (square), regressing only the narrow viewports
// (Desktop/Tablet are wide enough that max-width never clamps the width, so they
// were unaffected -- which is why only LegalPage Mobile/Print changed). We
// therefore mirror the rendition dimensions, falling back to the source image's
// intrinsic size if the rendition lacks them.
type IProps = Immutable<{
    value: Value;
}>;

export const ImageLinkBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const image = v.image;
    const rendition = image?.renditions["width-400"];
    return (
        <a href={v.page?.url ?? ""}>
            {image && (
                <img
                    src={rendition?.url ?? image.url}
                    alt={image.default_alt_text}
                    width={rendition?.width ?? image.width}
                    height={rendition?.height ?? image.height}
                    loading="lazy"
                    decoding="async"
                />
            )}
        </a>
    );
};
